import React from 'react';
import { connect } from 'react-redux';

import {
    showUxFeedbackWidget,
    actions as feedbackActions,
} from '@edu-frontend/modules/src/shared/lib/uxFeedbackWidget';
import { wrapError } from '@edu-frontend/modules/src/shared/lib/error';
import { showOtklick } from '@edu-frontend/modules/src/shared/lib/otklickWidget';
import { getMe } from 'platform-components/src/components/user/selectors';
import {
    withRouterPath,
    InjectedProps as WithRouterPathProps,
    InjectedProps as WithRouterPath,
} from 'platform-components/hocs';
import type { AppContext } from 'platform-components/src/components/app-factory/context';
import { bindActionCreators, Dispatch } from 'redux';

import { getCurrentYearCourseId } from 'common.components/course/selectors';
import LessonLoader from 'common.components/lesson-loader/lesson-loader';
import * as lessonViewActions from 'common.components/lesson-view.store/actions';
import * as lessonSelectors from 'common.components/lesson/selectors';
import { actions as coursesActions } from 'store/courses/actions';
import * as likesSelectors from 'store/likes/selectors';
import { getSearchStringCourseIdOrDefault } from 'utils/get-course';

import { ReduxTeacherStore, RootAction } from '../../../typings';
import * as actions from '../actions';
import * as selectors from '../selectors';

import './course-view__assigned-lessons.scss';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface HocProps {
    classId?: string;
    subject?: string;
}

interface AssignedLessonsProps extends StoreProps, DispatchProps, WithRouterPathProps {}

export class AssignedLessons extends React.Component<AssignedLessonsProps> {
    componentDidUpdate(prevProps: AssignedLessonsProps) {
        const { subject: prevSubject, classId: prevClassId, lessons: prevLessons } = prevProps;
        const { subject, classId, clearLessons, lessons } = this.props;

        if (subject !== prevSubject || prevClassId !== classId) {
            clearLessons();
        }

        // Показываем фидбэк после первой выдачи информатики
        // (он сам по дефолту не показывается больше одного раза)
        if (prevLessons !== lessons && subject === 'informatics' && lessons.length) {
            if (this.context.environment === 'production') {
                showUxFeedbackWidget(feedbackActions.AFTER_FIRST_ASSIGN);
            }

            showOtklick(feedbackActions.AFTER_FIRST_ASSIGN, (err) => {
                window.Ya.Rum.logError(
                    { message: 'Failed to showOtklick', additional: { eventName: feedbackActions.AFTER_FIRST_ASSIGN } },
                    wrapError(err)
                );
            });
        }
    }

    componentWillUnmount(): void {
        const { clearLessons } = this.props;

        clearLessons();
    }

    context: AppContext;

    loadLessons = () => {
        const { courseId } = this.props;

        if (!courseId) {
            return;
        }

        const { getLessonsRequest, getLessonsCountRequest } = this.props;

        getLessonsRequest(courseId, (clessons) => {
            const { getClessonReactions, classId, subject } = this.props;
            getClessonReactions(clessons, classId, subject);
        });
        getLessonsCountRequest({ courseId });
    };

    handleLessonClick = () => {
        const { toggleLessonModal } = this.props;

        toggleLessonModal(true);
    };

    checkShouldDisableLesson = (cLessonId: number) => {
        const { deletingLessonId } = this.props;

        return cLessonId === deletingLessonId;
    };

    render() {
        const { courseId, lessons, loadingStatus, shouldLoadLessons, shouldShowLikes, clessonLikes } = this.props;

        if (!courseId) {
            return null;
        }

        return (
            <LessonLoader
                canEditLessons={true}
                checkShouldDisableLesson={this.checkShouldDisableLesson}
                clessonLikes={clessonLikes}
                courseId={courseId}
                getNextPageRequest={this.loadLessons}
                lessons={lessons}
                lessonsMix="qa_assigned-lessons"
                loadingStatus={loadingStatus}
                onLessonClick={this.handleLessonClick}
                shouldLoadLessons={shouldLoadLessons}
                shouldShowLikes={shouldShowLikes}
            />
        );
    }
}

const mapStateToProps = (storeState: ReduxTeacherStore, ownProps: WithRouterPath & HocProps) => {
    const me = getMe(storeState);
    const classId = ownProps.classId ?? ownProps.match.params?.classId;
    const subject = ownProps.subject ?? ownProps.match.params?.subject;

    const currentYearCourseId = getCurrentYearCourseId(storeState, {
        classId,
        subject,
    });

    // специальный костыль для КМ-ов, чтобы они могли заходить в любые курсы по get-параметру
    const courseId = me ? getSearchStringCourseIdOrDefault(me, currentYearCourseId) : null;

    return {
        classId: Number(classId),
        subject,
        courseId,
        deletingLessonId: lessonSelectors.getDeletingCLessonId(storeState),
        lessons: selectors.getAssignedLessons(storeState, { courseId }),
        loadingStatus: selectors.getAssignedLessonsLoadingStatus(storeState, { courseId }),
        shouldLoadLessons: selectors.getShouldLoadAssignedLessons(storeState, { courseId }),
        shouldShowLikes: likesSelectors.getShouldShowLikeReactions(storeState, {
            classId,
            subject,
        }),
        clessonLikes: likesSelectors.getClessonTotalLikes(storeState),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<RootAction>) => {
    const { clearLessons, getAssignedLessonsRequest, getClessonReactionsRequest } = actions;
    const { getLessonsCountRequest } = coursesActions;
    const { toggleModal } = lessonViewActions;

    return bindActionCreators(
        {
            clearLessons,
            getLessonsCountRequest,
            getLessonsRequest: getAssignedLessonsRequest,
            toggleLessonModal: toggleModal,
            getClessonReactions: getClessonReactionsRequest,
        },
        dispatch
    );
};

export default withRouterPath(connect(mapStateToProps, mapDispatchToProps)(AssignedLessons));
